import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  Row,
  Col,
  CardTitle,
  CardText,
  Form,
  FormGroup,
  CustomInput,
  Button,
} from 'reactstrap';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

import api from '@src/services/api';
import * as masks from '@src/utility/Mask';
import source from '@src/assets/images/pages/create-account.svg';
import logo from '@src/assets/images/logo/logo.png';
import { useLoadingGlobal } from '@src/utility/hooks/useLoadingGlobal';
import { toastError, toastSuccess } from '@src/utility/Toast';

import '@styles/base/pages/page-auth.scss';

import './styles.scss';
import { useBrand } from '@src/utility/hooks/useBrand';

type CompanyData = {
  term: string;
  company: {
    socialReason: string;
    documentNumber: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
};

export const TermsServiceElohe: React.FC = () => {
  const { showLoader, hideLoader } = useLoadingGlobal();
  const { termId } = useParams<{ termId: string }>();

  const [companyData, setCompanyData] = React.useState<CompanyData | null>(
    null
  );
  const [hasAccept, setHasAccept] = useState(false);

  const getCompanyData = useCallback(async () => {
    try {
      showLoader();

      const { data: company } = await api.get<CompanyData>(
        `companies/terms/${termId}`
      );

      hideLoader();
      setCompanyData(company);
    } catch (error) {
      hideLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId]);

  const { nameTab } = useBrand();

  useEffect(() => {
    document.title = `Termos e Condições de Uso da Plataforma | ${nameTab}`;
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    if (termId) {
      getCompanyData();
    }
  }, [getCompanyData, termId, nameTab]);

  const Terms = () => {
    return (
      <strong>
        Eu li e aceito todos os Termos e Condições de Uso da Plataforma
      </strong>
    );
  };

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      try {
        showLoader();

        // const response = await axios.get<{
        //   IPv4: string;
        //   latitude: string;
        //   longitude: string;
        //   city: string;
        //   state: string;
        //   country_name: string;
        // }>('https://geolocation-db.com/json/');

        // await api.post(`companies/terms/${termId}`, {
        //   hasAccept,
        //   acceptationDate: moment().format('YYYY-MM-DD HH:mm:ss'),
        //   ipAddress: response.data.IPv4,
        //   latitude: String(response.data.latitude),
        //   longitude: String(response.data.longitude),
        //   city: response.data.city,
        //   regionName: response.data.state,
        //   country: response.data.country_name,
        // });

        await api.post(`companies/terms/${termId}`, {
          hasAccept,
          acceptationDate: moment().format('YYYY-MM-DD HH:mm:ss'),
          ipAddress: '',
          latitude: '',
          longitude: '',
          city: '',
          regionName: '',
          country: '',
        });

        hideLoader();

        toastSuccess({
          title: 'Parabéns',
          description:
            'Obrigado por aceitar todos os Termos e Condições de Uso da Plataforma!',
        });
      } catch (error) {
        hideLoader();

        const err = error as Error | AxiosError;

        if (axios.isAxiosError(err)) {
          const messages = err.response?.data;

          if (typeof messages === 'object') {
            if (!Object.keys(messages).includes('message')) {
              Object.keys(messages).forEach((message) => {
                const errors = messages[message];
                errors.forEach((error: string) => {
                  toastError({ title: 'Atenção', description: error });
                });
              });
            } else {
              toastError({ title: 'Atenção', description: messages.message });
            }
          } else {
            toastError({
              title: 'Atenção',
              description: 'Algo deu errado.',
            });
          }
        } else {
          toastError({ title: 'Atenção', description: 'Server error' });
        }
      }
    },
    [hasAccept, termId]
  );

  return (
    <div className="auth-wrapper auth-v2">
      <Row className="auth-inner m-0">
        <Link className="brand-logo" to="/" onClick={(e) => e.preventDefault()}>
          <img src={logo} alt="Logo Ultragate" className="img-fluid" />
        </Link>
        <Col className="d-none d-lg-flex align-items-center p-0" lg="3" sm="12">
          <div className="w-100 d-lg-flex align-items-center justify-content-center">
            <img className="img-fluid w-100" src={source} alt="Login Cover" />
          </div>
        </Col>

        <Col className="auth-bg px-2 px-sm-3 px-lg-5 pt-3" lg="9" sm="12">
          <Col className="px-xl-2 mx-auto" sm="8" md="10" lg="12">
            <article className="terms-elohe mb-2">
              <header className="entry-header mb-2">
                <CardTitle tag="h2" className="font-weight-bold">
                  Termos e Condições de Uso da Plataforma da ELOHE SOLUÇÕES LTDA
                </CardTitle>
              </header>

              <CardText className="mb-2">
                Por este instrumento, a pessoa identificada e qualificada no
                Cadastro, que é parte integrante deste Contrato{' '}
                <strong>{companyData?.company.socialReason}</strong>, CNPJ{' '}
                <strong>
                  {masks.cpfOrCnpjMask.mask(
                    companyData?.company.documentNumber ?? ''
                  )}
                </strong>
                , Endereço <strong>{companyData?.address.street}</strong>,
                Cidade <strong>{companyData?.address.city}</strong>, UF{' '}
                <strong>{companyData?.address.state}</strong>, CEP:{' '}
                <strong>
                  {masks.cepMask.mask(companyData?.address.zipCode ?? '')}
                </strong>{' '}
                (“USUÁRIO”); e <strong>ELOHE SOLUÇÕES LTDA</strong>. inscrita no
                CNPJ/MF sob o nº 10.268.349/0001-62, com sede na ST CENTRAL C7
                LOTE 11 - Bairro <strong>TAGUATINGA CENTRO</strong>, na cidade
                de <strong>BRASÍLIA</strong>, estado de{' '}
                <strong>DISTRITO FEDERAL</strong>, CEP: 72.010-050 (“PARCEIRA”);
                têm entre si justo e acordado este Termo e Condições de Uso da
                Plataforma (“Contrato”), nos termos e condições abaixo.
              </CardText>
              <CardText className="mb-2">
                Os serviços de tecnologia relacionados com soluções de pagamento
                por meios eletrônicos, gestão e custódia de recursos e outros
                serviços financeiros, são prestados em parceria com FLAGSHIP
                INSTITUIÇÃO DE PAGAMENTO LTDA. (“FLAGSHIP”), inscrita no CNPJ
                sob o nº 23.114.447/0001-97, com sede na Rua Cardeal Arcoverde,
                2365, conjunto 12, Bairro Pinheiros, CEP 05.407-003, na cidade
                de São Paulo, estado de São Paulo (“Instituição de Pagamento”).
              </CardText>
              <CardText className="mb-2">
                Ao aceitar eletronicamente este Contrato, com a marcação da
                caixa de diálogo “Li e concordo com os Termos e Condições”, o
                USUÁRIO estará automaticamente aderindo e concordando com os
                termos e condições deste Contrato e da Política de Privacidade.
                A utilização do Sistema ou de qualquer Funcionalidade será
                interpretada como o aceite pleno a este Contrato. A Plataforma
                também poderá ser utilizada para que Fornecedores ofereçam
                produtos e serviços. Neste caso, a PARCEIRA atuará como mera
                intermediadora, disponibilizando tecnologia para aproximação
                entre os Fornecedores e o USUÁRIO. Este Contrato poderá ser
                periodicamente revisto e alterado, podendo o USUÁRIO, caso não
                concorde com a modificação, denunciá-lo sem quaisquer ônus ou
                penalidades.
              </CardText>
              <CardText className="mb-2">1. Definições</CardText>
              <CardText className="mb-2">
                1.1. As palavras e expressões abaixo, indicadas neste Contrato
                pela primeira letra maiúscula, terão as seguintes definições:
              </CardText>

              <CardText className="mb-2">
                “Cadastro”:formulário preenchido pelo USUÁRIO na Plataforma,
                contendo seus dados pessoais e demais informações necessárias
                para credenciamento ao Sistema e criação da Conta de Pagamento
              </CardText>

              <CardText className="mb-2">
                “Cartão Pré-Pago”: Cartão plástico que, caso solicitado pelo
                USUÁRIO, poderá ser utilizado mediante recursos previamente
                aportados na Conta de Pagamento do USUÁRIO, para a aquisição de
                bens ou serviços, em território nacional, perante os
                estabelecimentos filiados à bandeira indicada no respectivo
                Cartão, cuja emissão será realizada de acordo com os Termos e
                Condições de Uso do Cartão, o qual é anexo deste Termo.
              </CardText>

              <CardText className="mb-2">
                “Conta de Pagamento”: conta de titularidade do USUÁRIO,
                destinada ao carregamento, transferência e resgate de recursos,
                cujos valores, convertidos em moeda eletrônica, serão geridos e
                custodiados pela Instituição de Pagamento.
              </CardText>

              <CardText className="mb-2">
                “Contrato”: este - Termos e Condições de Uso da Plataforma-, que
                é um contrato eletrônico disponível na plataforma da PARCEIRA em
                endereço eletrônico a ser informado pela PARCEIRA em seu site:
                www.ultragate.com.br/termosdeuso
              </CardText>
              <CardText className="mb-2">
                “Disputa”: procedimento de análise do pedido de cancelamento ou
                estorno da Transação feito pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                “Fornecedores”: terceiros que oferecem produtos e/ou serviços ao
                USUÁRIO por meio da Plataforma.
              </CardText>
              <CardText className="mb-2">
                “Funcionalidades”: tecnologias disponibilizadas na Plataforma,
                para a realização de Transações pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                “Plataforma”: site na internet disponível pela PARCEIRA em
                www.app.ultragate.com.br, ou aplicativo para dispositivos móveis
                (caso existente), todos de responsabilidade e titularidade da
                PARCEIRA, disponibilizado ao USUÁRIO para a utilização das
                Funcionalidades e realização de Transações.
              </CardText>
              <CardText className="mb-2">
                “Política de Privacidade”: política disponível no site da
                PARCEIRA em www.ultragate.com.br/politicadeprivacidade que é
                integrante deste Contrato, a qual dispõe sobre a coleta,
                utilização, armazenamento, tratamento, compartilhamento,
                proteção e eliminação das informações do USUÁRIO, em decorrência
                da utilização do Sistema.
              </CardText>
              <CardText className="mb-2">
                “Serviços de Terceiros”: produtos e serviços disponíveis ao
                USUÁRIO na Plataforma, e que serão fornecidos pelos Fornecedores
                credenciados.
              </CardText>
              <CardText className="mb-2">
                “Sistema”: serviços relacionados à abertura de Conta de
                Pagamento e realização de Transações pelo USUÁRIO, incluindo a
                disponibilização de informações e fornecimento de extratos por
                meio da Plataforma.
              </CardText>
              <CardText className="mb-2">
                “Transação”: operação em que o USUÁRIO realiza a movimentação de
                sua Conta de Pagamento, realizando o carregamento de recursos em
                sua Conta de Pagamento; a transferência de recursos para a Conta
                de Pagamento de outros USUÁRIOS credenciados no Sistema; ou o
                resgate de recursos do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                “USUÁRIO”: pessoa jurídica ou pessoa física que, ao aderir ao
                presente Contrato, está habilitada a realizar Transações por
                meio do Sistema.
              </CardText>
              <CardText className="mb-2">2. Objeto</CardText>
              <CardText className="mb-2">
                2.1. Este Contrato regula a prestação de serviços de tecnologia,
                pela PARCEIRA, individualmente ou por intermédio da Instituição
                de Pagamento, para: (i) cadastro e credenciamento do USUÁRIO ao
                Sistema; (ii) criação de Conta de Pagamento, habilitando o
                USUÁRIO a realizar Transações para carregamento, transferência e
                resgate de recursos; e (iii) gestão e custódia dos recursos
                mantidos na Conta de Pagamento de titularidade do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                2.1.1. Ainda, os serviços prestados incluem o fornecimento de
                tecnologia, como mera intermediária, para que o USUÁRIO possa
                contratar Serviços de Terceiros.
              </CardText>
              <CardText className="mb-2">
                2.2. Por meio da Plataforma, o USUÁRIO poderá: (i) realizar o
                carregamento e resgate dos recursos disponíveis em Conta de
                Pagamento; (ii) verificar o saldo e extrato de movimentações
                relacionadas à Conta de Pagamento; (iii) realizar transferências
                entre USUÁRIOS detentores de Conta de Pagamento; (iv) efetuar
                ordem de transferência para conta bancária, de sua titularidade
                ou de terceiros (por TED, TEF ou DOC); (v) realizar pagamentos
                instantâneos por QRCode, caso disponível; (vi) emitir boletos
                bancários; (vii) realizar pagamentos de boletos bancários e
                contas de consumo; (viii) realizar recargas de serviços
                pré-pagos, incluindo, mas não se limitando a telefonia móvel /
                fixa, transporte público, lojas de aplicativos, dentre outros;
                (ix) realizar saques em ATMs, conforme disponibilidade; (xi)
                solicitar emissão de cartão pré-pago, conforme disponibilidade;
                (x) utilizar de outras Funcionalidades disponíveis.
              </CardText>
              <CardText className="mb-2">
                2.2.1. As Funcionalidades disponíveis na Plataforma poderão, a
                qualquer momento e sem necessidade de aviso prévio, ser
                excluídas, alteradas, modificadas ou ajustadas em horários de
                disponibilidades dos serviços de acordo com as regras da
                Plataforma.
              </CardText>
              <CardText className="mb-2">
                2.3. Os serviços serão prestados de forma remota, mediante a
                licença de uso das Funcionalidades disponibilizadas ao USUÁRIO
                na Plataforma.
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                2.4. Em contraprestação à utilização do Sistema, realização das
                Transações e demais serviços prestados, o USUÁRIO pagará as
                tarifas estabelecidas neste Contrato, de acordo com os valores
                informados no momento do Cadastro e/ou para consulta na
                Plataforma.
              </CardText>
              <CardText className="mb-2">
                2.4.1. A contratação de serviços específicos prestados por
                Fornecedores (conforme disponíveis na Plataforma) será
                estabelecida em instrumentos contratuais próprios.
              </CardText>
              <CardText className="mb-2">
                2.5. A Instituição de Pagamento, sob sua única e exclusiva
                responsabilidade, poderá subcontratar terceiros ou realizar
                parcerias para a prestação de parte dos serviços que integram o
                Sistema, sendo responsável por tal ato.
              </CardText>
              <CardText className="mb-2">
                2.6. Em razão deste Contrato, não se garante a ininterrupção e a
                velocidade do Sistema e/ou da Plataforma, que poderá apresentar
                indisponibilidade, lentidão e erros de processamento, inclusive
                por tempo indeterminado, em casos de manutenção preventiva ou
                corretiva, falha de operação, erro de sistema, falhas de outros
                prestadores de serviços e eventos casos fortuitos ou força
                maior.
              </CardText>
              <CardText className="mb-2">
                2.6.1. A PARCEIRA não será responsável pela: (i) intermitência
                ou indisponibilidade de conexão à internet adotada pelo USUÁRIO;
                (ii) incapacidade técnica do dispositivo móvel ou sistema
                operacional; (iii) indisponibilidade da Plataforma na loja de
                aplicativos ou navegador de internet utilizados pelo USUÁRIO;
                e/ou (iv) atividades de pessoas não autorizadas a utilizar o
                Sistemas ou a Plataforma.
              </CardText>
              <CardText className="mb-2">3. Credenciamento ao Sistema</CardText>
              <CardText className="mb-2">
                3.1. O credenciamento ao Sistema será realizado pela adesão do
                USUÁRIO a este Contrato, que se efetivará pelo: (i)
                preenchimento do Cadastro; e (ii) pelo aceite eletrônico
                expressamente manifestado na Plataforma.
              </CardText>
              <CardText className="mb-2">
                3.2. Para utilização do Sistema, o USUÁRIO deverá
                obrigatoriamente preencher o Cadastro, fornecendo seus dados
                pessoais e informações que venham a ser solicitados na
                Plataforma, sendo, no mínimo:
              </CardText>
              <CardText className="mb-2">
                (a) Para pessoa física, deverão ser fornecidos: nome completo e
                número de inscrição no CPF/MF válido e ativo; e
              </CardText>
              <CardText className="mb-2">
                (b) Para pessoa jurídica, deverão ser fornecidos: razão social
                ou denominação, número de inscrição no CNPJ/MF válido e ativo, e
                as informações acima indicadas para seus representantes,
                mandatários ou prepostos autorizados.
              </CardText>
              <CardText className="mb-2">
                3.2.1. A Instituição de Pagamento poderá limitar a utilização do
                Sistema, de acordo com o valor e a quantidade de Transações.
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                3.2.2. Sempre que necessário, inclusive para possibilitar a
                utilização do Sistema para realização de Transações em valor e
                quantidade aos limites estabelecidos, a PARCEIRA poderá
                solicitar que o USUÁRIO forneça informações complementares
                àquelas indicadas no Cadastro, bem como poderá, a qualquer
                momento, e ao seu exclusivo critério, solicitar cópias de
                documentos para averiguar a veracidade das informações prestadas
                pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                3.2.3. Na hipótese de se verificar dados incorretos ou
                inverídicos fornecidos pelo USUÁRIO ou, ainda, caso o USUÁRIO se
                recuse ou se omita a enviar as informações e documentos
                solicitados, a Instituição de Pagamento poderá temporariamente
                bloquear o acesso ao Sistema e impedir a utilização das
                Funcionalidades até que haja a regularização.
              </CardText>
              <CardText className="mb-2">
                3.2.4. O USUÁRIO autoriza a realização de pesquisas, em base de
                dados públicas ou privadas, com a finalidade de verificar a
                veracidade dos dados e informações indicadas no Cadastro.
              </CardText>
              <CardText className="mb-2">
                3.3. O USUÁRIO se responsabiliza civil e criminalmente pela
                veracidade das informações prestadas, inclusive perante
                terceiros, obrigando-se a manter seus dados atualizados.
              </CardText>
              <CardText className="mb-2">
                3.3.1. A PARCEIRA e a Instituição de Pagamento não serão
                responsáveis por erros ou pela inexecução dos serviços que
                integram o Sistema caso o USUÁRIO preste informações inexatas,
                inverídicas ou desatualizadas.
              </CardText>
              <CardText className="mb-2">
                3.4. O USUÁRIO, quando do preenchimento do Cadastro ou primeiro
                acesso ao Sistema, deverá cadastrar login e uma senha para
                utilização das Funcionalidades e realização das Transações.
              </CardText>
              <CardText className="mb-2">
                3.4.1. O uso do login e senha são de uso pessoal, exclusivo e
                intransferível pelo USUÁRIO, que deverá mantê-los confidenciais
                e não permitir seu acesso por terceiros.
              </CardText>
              <CardText className="mb-2">
                3.4.2. O USUÁRIO, na qualidade de pessoa jurídica, se compromete
                a somente dar acesso ao login e senha para seus representantes
                legais, sócios, administradores e/ou prepostos com poderes para
                celebrar negócios jurídicos em seu nome; sendo responsável,
                perante à PARCEIRA, outros USUÁRIOS, a Instituição de Pagamento
                e terceiros, por todos os atos e negócios realizados por meio da
                utilização do Sistema.
              </CardText>
              <CardText className="mb-2">
                3.4.3. O USUÁRIO deverá informar um e-mail válido para
                comunicação com a PARCEIRA; sendo que qualquer comunicação ou
                notificação enviada por e-mail será considerada válida e eficaz
                entre as Partes.
              </CardText>
              <CardText className="mb-2">
                3.5. Para utilizar os serviços, o USUÁRIO, dentre outras
                obrigações previstas neste Contrato, deve ser: (i) pessoa
                física, maior de 18 (dezoito) anos e possuir inscrição válida e
                regular perante a Receita Federal do Brasil; ou (ii) pessoa
                jurídica devidamente constituída, com sede ou escritório no
                Brasil e possuir inscrição válida e regular perante a Receita
                Federal do Brasil.
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                3.5.1. Em razão da natureza dos serviços prestados, a PARCEIRA
                não possui condições de verificar a capacidade civil e
                regularidade do USUÁRIO; de forma que não responderá por
                quaisquer prejuízos que venham a ser causados em razão da
                inobservância das condições acima indicadas.
              </CardText>
              <CardText className="mb-2">
                3.6. É vedada a utilização do Sistema e a realização das
                Transações para a celebração de negócios: (i) considerados
                ilícitos, nos termos da legislação brasileira; (ii) que importem
                em violação ao Sistema Financeiro Nacional e às normas do Banco
                Central do Brasil e às regras das instituições financeiras,
                bandeiras, credenciadoras e emissores de cartões de crédito e
                débito; (iii) considerados como crimes financeiros, com o
                intuito de lavagem de dinheiro, financiamento ao terrorismo e
                corrupção, dentre outros crimes correlatos, ainda que
                indiretamente; (iv) que não representem um negócio jurídico
                regular e tenham por intenção a prática de fraudes; ou (v) que,
                de qualquer modo, venham a causar prejuízos à PARCEIRA,
                Instituição de Pagamento, demais USUÁRIOS ou terceiros.
              </CardText>
              <CardText className="mb-2">
                3.7. As Transações com indícios ou suspeitas de fraude estarão
                sujeitas ao não processamento ou ao cancelamento, ainda que
                realizada de forma conivente ou não pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                3.8. A Instituição de Pagamento poderá suspender o acesso às
                Funcionalidades e deixar de realizar as Transações pelo Sistema
                sempre que identificar ou entender que a atividade do USUÁRIO ou
                natureza das Transações viola qualquer dispositivo deste
                Contrato ou da legislação vigente; podendo sujeitar o USUÁRIO ao
                cancelamento do seu credenciamento e sua exclusão imediata do
                Sistema, independentemente de qualquer aviso ou notificação
                prévia, não gerando ao USUÁRIO qualquer tipo de indenização ou
                ressarcimento.
              </CardText>
              <CardText className="mb-2">4. Conta de Pagamento</CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                4.1. Ao realizar o Cadastro na Plataforma e aderir a este
                Contrato, o USUÁRIO concorda com a abertura de Conta de
                Pagamento individual e exclusiva, de sua exclusiva titularidade,
                a qual poderá ser movimentada por meio das Funcionalidades.
              </CardText>
              <CardText className="mb-2">
                4.1.1. O credenciamento do USUÁRIO e a abertura da Conta de
                Pagamento ocorrerá após a aprovação do Cadastro realizado na
                Plataforma, mesmo que não haja: (i) o aporte prévio de recursos;
                e (ii) a realização de qualquer Transação no Sistema.
              </CardText>
              <CardText className="mb-2">
                4.2. O carregamento da Conta de Pagamento se dará por um dos
                meios disponíveis no Sistema, de livre escolha do USUÁRIO,
                dentre os quais:
              </CardText>
              <CardText className="mb-2">
                (a) Pagamento de boleto bancário, pelo próprio USUÁRIO - ou
                terceiros em seu favor do USUÁRIO -, com identificação única que
                permita o carregamento do valor pago na Conta de Pagamento;
              </CardText>
              <CardText className="mb-2">
                (b) Transferência bancária realizada pelo próprio USUÁRIO - ou
                terceiros em favor do USUÁRIO -, mediante operações de TEF, DOC
                ou TED, se disponível;
              </CardText>
              <CardText className="mb-2">
                (c) Recebimento por meio de transferências realizadas por outros
                USUÁRIOS, no âmbito do Sistema ou por outros meios de pagamento
                disponíveis; e
              </CardText>
              <CardText className="mb-2">
                (d) Recebimento por meio de pagamento instantâneo - PIX, quando
                disponível.
              </CardText>
              <CardText className="mb-2">
                4.2.1. A Instituição de Pagamento poderá, a qualquer momento,
                restringir ou excluir uma das formas de carregamento
                disponíveis, ou estipular outras formas de carregamento da Conta
                de Pagamento pelo USUÁRIO, mediante alteração deste Contrato e
                disponibilidade por meio das Funcionalidades.
              </CardText>
              <CardText className="mb-2">
                4.2.2. Com o carregamento da Conta de Pagamento, por uma das
                modalidades permitidas, os recursos estarão disponíveis no
                Sistema em até 01 (um) dia útil contado do efetivo recebimento
                dos recursos pela Instituição de Pagamento; sendo possível ao
                USUÁRIO, a partir de então, realizar as Transações por meio das
                Funcionalidades.
              </CardText>
              <CardText className="mb-2">
                4.2.3. O USUÁRIO declara-se ciente de que os boletos bancários
                emitidos irão indicar como beneficiário a Instituição de
                Pagamento, que, após o recebimento dos recursos, irá realizar o
                carregamento da Conta de Pagamento.
              </CardText>
              <CardText className="mb-2">
                4.3. Os recursos depositados na Conta de Pagamento poderão ser
                utilizados para transferência ou resgate, por um dos meios
                disponíveis no Sistema, dentre os quais:
              </CardText>
              <CardText className="mb-2">
                (a) Realização de Transações de transferência para a Conta de
                Pagamento de outros USUÁRIOS credenciados no Sistema;
              </CardText>
              <CardText className="mb-2">
                (b) Pagamento de débitos do USUÁRIO em razão da compra de
                produtos ou serviços contratados na Plataforma;
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                (c) Pagamento de boletos bancários e contas de consumo;
              </CardText>
              <CardText className="mb-2">
                (d) Realização de recargas de serviços pré-pagos, incluindo, mas
                não se limitando a telefonia móvel / fixa, transporte público,
                lojas de aplicativos, se disponível, dentre outras;
              </CardText>
              <CardText className="mb-2">
                (e) Realização de saques em ATMs, conforme disponibilidade;
              </CardText>
              <CardText className="mb-2">
                (f) Resgate de recursos, mediante transferência para a conta
                bancária de titularidade do USUÁRIO; ou, caso disponível, para a
                conta bancária de terceiros que não se encontram cadastrados no
                Sistema;
              </CardText>
              <CardText className="mb-2">
                (g) Carregamento de cartão pré-pago, caso disponível; e
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                (h) Pagamentos e transferências instantâneas realizadas por meio
                do PIX, quando disponível
              </CardText>
              <CardText className="mb-2">
                4.3.1. A transferência dos recursos entre Contas de Pagamento no
                âmbito do Sistema será realizada em até 1 (um) dia útil contado
                da realização da Transação.
              </CardText>
              <CardText className="mb-2">
                4.3.2. O resgate de recursos, por meio de transferência
                bancária, será realizado em até 02 (dois) dias úteis contado da
                realização da Transação.
              </CardText>
              <CardText className="mb-2">
                4.3.3. As Transações realizadas por meio das Funcionalidades
                deixarão de ser acatadas pela Instituição de Pagamento quando:
                (i) não houver recursos suficientes na Conta de Pagamento; (ii)
                o USUÁRIO deixar de fornecer as informações suficientes ou
                fornecer informações incorretas para realização da Transação;
                e/ou (iii) houver indícios de fraude ou suspeita ou ato ilícito,
                de acordo com os termos previstos neste Contrato e na legislação
                vigente.
              </CardText>
              <CardText className="mb-2">
                4.4. A Instituição de Pagamento poderá determinar limites de
                valor mínimo e máximo para o carregamento das Contas de
                Pagamento e para a realização das Transações, que poderá variar
                de acordo com as informações de Cadastro do USUÁRIO, o tipo de
                Transação, ou outro critério definido pela Instituição de
                Pagamento.
              </CardText>
              <CardText className="mb-2">
                4.4.1. Os critérios acima indicados poderão ser alterados a
                qualquer momento pela Instituição de Pagamento, com antecedência
                de 05 (cinco) dias, e mediante publicação na Plataforma.
              </CardText>
              <CardText className="mb-2">
                4.5. Os recursos creditados na Conta de Pagamento do USUÁRIO
                serão mantidos em conta bancária de titularidade da Instituição
                de Pagamento, em instituição financeira de primeira linha, e,
                nos termos do art. 12 da Lei 12.865/2013, (i) constituem
                patrimônio separado, que não se confunde com o da Instituição de
                Pagamento; (ii) não respondem direta ou indiretamente por
                nenhuma obrigação da Instituição de Pagamento, nem podem ser
                objeto de arresto, sequestro, busca e apreensão ou qualquer
                outro ato de constrição judicial em função de débitos de
                responsabilidade da Instituição de Pagamento; (iii) não podem
                ser dados em garantia de débitos assumidos pela Instituição de
                Pagamento; e (iv) não compõem o ativo da Instituição de
                Pagamento, para efeito de falência ou liquidação judicial ou
                extrajudicial.
              </CardText>
              <CardText className="mb-2">
                4.6. Os ônus ou bônus decorrentes da manutenção dos recursos em
                conta bancária de titularidade da Instituição de Pagamento não
                poderão ser imputáveis ao USUÁRIO, assim como por ele
                reclamados.
              </CardText>
              <CardText className="mb-2">
                4.6.1. Os recursos mantidos na Conta de Pagamento, salvo se
                expressamente pactuado de modo diverso, não sofrerão qualquer
                tipo de acréscimo ou alteração, tais como correção monetária e
                juros; e nem haverá o pagamento de qualquer remuneração ao
                USUÁRIO, independentemente do período que ficarem depositados.
              </CardText>
              <CardText className="mb-2">
                4.7. Os valores depositados na Conta de Pagamento devem ser
                utilizados para pagamentos e transferências, sendo considerados
                pela Instituição de Pagamento recursos em trânsito de
                titularidade do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                4.8. O USUÁRIO não poderá ceder ou onerar, a qualquer título, os
                direitos sobre os recursos depositados em sua Conta de
                Pagamento, sem a prévia e escrita autorização da Instituição de
                Pagamento, sob pena de ineficácia da cessão.
              </CardText>
              <CardText className="mb-2">
                4.9. O USUÁRIO terá acesso às Transações realizadas ou pendentes
                de pagamento pelo acesso ao extrato de sua Conta de Pagamento,
                podendo visualizar no Sistema, o saldo e histórico das
                movimentações. A disponibilização do saldo e do extrato das
                movimentações caracteriza-se como prestação de contas, para
                todos os fins legais.
              </CardText>
              <CardText className="mb-2">
                4.9.1. Apenas será disponibilizado o acesso às Transações
                realizadas a cada período de 12 (doze) meses, cabendo ao USUÁRIO
                o controle e arquivo, inclusive com a possibilidade de impressão
                do extrato disponibilizado.
              </CardText>
              <CardText className="mb-2">
                4.10. O USUÁRIO declara-se ciente de que os serviços previstos
                neste Contrato se destinam tão somente a efetivar pagamentos e
                recebimentos em moeda nacional, bem como assegura que todos os
                recursos movimentados em sua Conta de Pagamento serão oriundos
                de fontes lícitas e declaradas, isentando a PARCEIRA e a
                Instituição de Pagamento de qualquer responsabilidade.
              </CardText>

              <CardText className="mb-2">
                5. Disputa e Cancelamento das Transações
              </CardText>

              <CardText className="mb-2">
                5.1. A Transação realizada entre os USUÁRIOS credenciados no
                Sistema, mediante a transferência de recursos em moeda
                eletrônica, será automaticamente cancelada sempre que: (i) tiver
                sido processada incorretamente pelo USUÁRIO, em razão de
                informações errôneas indicadas pelo USUÁRIO no momento da
                realização da Transação; (ii) foi realizada em desconformidade
                com as disposições deste Contrato, ou (iii) haja suspeita de
                fraude, ato ilícito ou qualquer irregularidade.
              </CardText>
              <CardText className="mb-2">
                5.1.1. Não será possível o cancelamento ou abertura de Disputa
                para as Transações de carregamento ou resgate realizadas fora do
                âmbito do Sistema, ou seja, antes da conversão dos recursos em
                moeda eletrônica ou após o repasse de valores pela Instituição
                de Pagamento.
              </CardText>
              <CardText className="mb-2">
                5.2. O USUÁRIO declara e garante que será integralmente
                responsável pela veracidade, precisão e conformidade das
                informações e valores das relações comerciais relacionadas com
                as Transações; respondendo, se o caso, pela qualidade,
                quantidade, segurança, adequação, preço, prazo, entrega,
                Funcionalidade e garantias dos produtos ou serviços que deram
                origem às Transações.
              </CardText>
              <CardText className="mb-2">
                5.3. Todas as reclamações e contestações decorrentes de
                quaisquer Transações realizadas no âmbito do Sistema deverão ser
                dirimidas diretamente entre os USUÁRIOS; de modo que a PARCEIRA
                e a Instituição de Pagamento estarão isentas de qualquer
                responsabilidade, e sem prejuízo da possibilidade de retenção
                e/ou compensação na forma prevista neste Contrato.
              </CardText>
              <CardText className="mb-2">
                5.4. Caso o USUÁRIO apresente contestação de uma Transação, será
                iniciado o procedimento de Disputa, com a retenção do valor da
                Transação até a solução da Disputa.
              </CardText>
              <CardText className="mb-2">
                5.4.1. A Disputa deverá ser aberta pelo USUÁRIO no prazo máximo
                de 10 (dez) dias a contar da data de realização da Transação,
                diretamente na central de atendimento da PARCEIRA disponível na
                Plataforma; sendo certo que a abertura da Disputa não garante a
                restituição dos valores em discussão. 5.4.2. O pedido de
                abertura de Disputa deverá ser devidamente fundamentado e
                acompanhado da documentação pertinente.
              </CardText>
              <CardText className="mb-2">
                5.4.2. O pedido de abertura de Disputa deverá ser devidamente
                fundamentado e acompanhado da documentação pertinente.
              </CardText>
              <CardText className="mb-2">
                5.4.3. Com a abertura do procedimento de Disputa, será
                solicitado aos USUÁRIOS envolvidos, explicações e documentos que
                comprovam a realização do negócio que deu origem à Transação.
              </CardText>
              <CardText className="mb-2">
                5.4.4. Caberá unicamente à Instituição de Pagamento, por
                critérios próprios, analisar a documentação e decidir sobre a
                Disputa, em até 30 (trinta) dias contados do envio das
                informações e documentos pelo último USUÁRIO comunicado.
              </CardText>
              <CardText className="mb-2">
                5.4.5. Caso a contestação seja acolhida, haverá o estorno do
                valor respectivo da Conta de Pagamento do USUÁRIO que iniciou a
                Disputa. Se a contestação não for acolhida, a Transação
                realizada será mantida.
              </CardText>
              <CardText className="mb-2">
                5.4.6. Caso o USUÁRIO que solicitou a abertura da Disputa deixe
                de apresentar as informações e documentos solicitados, o
                procedimento será encerrado automaticamente.
              </CardText>
              <CardText className="mb-2">
                5.5. Caso se identifique níveis excessivos de pedido de abertura
                de Disputa perante o USUÁRIO, a Instituição de Pagamento poderá:
                (i) realizar a retenção, total ou parcialmente, dos valores
                existentes na Conta de Pagamento, como garantia para cobrir
                potenciais danos; e (ii) suspender ou inabilitar permanentemente
                o acesso do USUÁRIO à Plataforma.
              </CardText>
              <CardText className="mb-2">
                5.6. As Transações serão canceladas, automaticamente, quando:
                (i) for constatada sua duplicidade; (ii) houver a indicação de
                informações incompletas ou imprecisas; (iii) for constatado
                indícios de irregularidade ou fraude; (iv) nos demais casos
                previstos neste Contrato.
              </CardText>

              <CardText className="mb-2">
                6. Hipóteses de Retenção e Compensação de Recursos
              </CardText>

              <CardText className="mb-2">
                6.1. O USUÁRIO reconhece e concorda que a Instituição de
                Pagamento, em conformidade com as disposições deste Contrato,
                terá o direito de: (i) reter os valores mantidos na Conta de
                Pagamento do USUÁRIO para garantir, de forma integral, quaisquer
                pagamentos que sejam devidos ou para o resguardo contra riscos
                financeiros relacionados às obrigações do USUÁRIO; e (ii)
                compensar, com os valores mantidos na Conta de Pagamento, os
                débitos do USUÁRIO, de qualquer natureza.
              </CardText>
              <CardText className="mb-2">
                6.2. Será realizada a retenção e compensação dos valores,
                existentes ou futuros, mantidos na Conta de Pagamento do
                USUÁRIO, nas seguintes hipóteses:
              </CardText>
              <CardText className="mb-2">
                (a) Quando se verificar um alto nível de risco operacional ou de
                crédito, associado ao histórico de Transações realizadas pelo
                USUÁRIO;
              </CardText>
              <CardText className="mb-2">
                (b) Havendo indícios de irregularidade ou risco de cancelamento
                da Transação, em razão de denúncias, contestação, Disputa ou
                pelo uso inadequado do Sistema;
              </CardText>
              <CardText className="mb-2">
                (c) Nos casos em que se verificar a iliquidez, insolvência,
                pedido de recuperação judicial ou extrajudicial, pedido de
                falência, encerramento de atividades ou qualquer outra hipótese
                em que ficar caracterizada a dificuldade do USUÁRIO em cumprir
                suas obrigações contratuais e/ou legais;
              </CardText>
              <CardText className="mb-2">
                (d) Sempre que houver o descumprimento das obrigações previstas
                neste Contrato ou na legislação vigente; ou
              </CardText>
              <CardText className="mb-2">
                (e) Para cumprimento de ordens judiciais ou administrativas.
              </CardText>
              <CardText className="mb-2">
                6.3. Caso não haja saldo suficiente para arcar com o pagamento
                dos débitos devidos, o USUÁRIO será comunicado pela PARCEIRA
                para proceda o carregamento imediato de sua Conta de Pagamento,
                sob pena de caracterização de sua mora, automaticamente e sem a
                necessidade de aviso ou qualquer formalidade.
              </CardText>
              <CardText className="mb-2">
                6.3.1. A ausência ou atraso no pagamento de quaisquer valores
                devidos pelo USUÁRIO, ensejará no pagamento de multa moratória
                de 2% (dois por cento), correção monetária pelo IGPM/FGV ou
                outro índice que vier a substituí-lo, e juros de 1% (um por
                cento) ao mês, a serem calculados sobre a quantia devida.
              </CardText>
              <CardText className="mb-2">
                6.3.2. A inadimplência do USUÁRIO ensejará, ainda, na rescisão
                imediata deste Contrato e na adoção das medidas legais para a
                cobrança do débito, inclusive a inclusão da dívida perante os
                órgãos de proteção ao crédito.
              </CardText>
              <CardText className="mb-2">
                6.4. O USUÁRIO terá o prazo de 60 (sessenta) dias para apontar
                eventual divergência ou incorreção em relação aos valores
                lançados na Conta de Pagamento, a contar da realização da
                Transação, lançamento do débito ou compensação. Após esse prazo,
                o USUÁRIO não mais poderá reclamar dos lançamentos realizados,
                concedendo plena e definitiva quitação.
              </CardText>

              <CardText className="mb-2">
                7. Resgate de Recursos e Encerramento da Conta de Pagamento
              </CardText>

              <CardText className="mb-2" />
              <CardText className="mb-2">
                7.1. O USUÁRIO poderá, a qualquer momento, desde que possua
                saldo suficiente para arcar com a tarifa de saque, as tarifas
                bancárias aplicáveis e o pagamento de eventuais débitos
                contraídos em razão deste Contrato, efetuar o resgate integral
                dos recursos mantidos na Conta de Pagamento, bem como
                encerrá-la, mediante solicitação à PARCEIRA via Sistema.
              </CardText>
              <CardText className="mb-2">
                7.2. O resgate de recursos será realizado a pedido do USUÁRIO,
                mediante o repasse do valor líquido e em moeda nacional, de
                acordo com as formas estabelecidas para utilização dos recursos
                mantidos em Conta de Pagamento.
              </CardText>
              <CardText className="mb-2">
                7.3. Caso disponível, o USUÁRIO também poderá solicitar que o
                crédito decorrente das Transações seja transferido para a conta
                corrente de terceiros, por conta e ordem do USUÁRIO.
              </CardText>
              <CardText className="mb-2" />
              <CardText className="mb-2">
                7.4. O USUÁRIO se responsabiliza pela exatidão dos dados
                informados sobre a conta bancária, de sua titularidade ou de
                terceiros (caso disponível); isentando a PARCEIRA e a
                Instituição de Pagamento de qualquer responsabilidade pelas
                transferências realizadas em razão de informações imprecisas ou
                inexatas que venham a ser informadas pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                7.4.1. Caso não seja possível o resgate de recursos por
                irregularidade na conta bancária indicada, os respectivos
                valores permanecerão retidos e serão mantidos na Conta de
                Pagamento até que haja a regularização pelo USUÁRIO, sem a
                incidência de quaisquer ônus, penalidades ou encargos.
              </CardText>
              <CardText className="mb-2">
                7.5. Quando decorrente de falha técnica e/ou operacional no
                Sistema ou no sistema bancário, a Instituição de Pagamento
                poderá, sem incorrer em qualquer ônus ou penalidade, exceder, em
                até 01 (um) dia útil, o prazo estabelecido para efetuar o
                resgate da Conta de Pagamento.
              </CardText>
              <CardText className="mb-2">
                7.6. Na hipótese de a data prevista para o resgate de recursos
                ser considerada feriado ou em dia de não funcionamento bancário,
                o pagamento será realizado no 1º (primeiro) dia útil
                subsequente.
              </CardText>
              <CardText className="mb-2">
                7.7. A não utilização da Conta de Pagamento, definida como a
                ausência de qualquer utilização pelo prazo de 06 (seis) meses,
                ensejará na cobrança de tarifa para o ressarcimento das despesas
                com manutenção, e que será descontada do saldo existente na
                Conta de Pagamento.
              </CardText>
              <CardText className="mb-2">
                7.8. A partir do momento em que o USUÁRIO não possuir saldo em
                sua conta por mais de 30 (trinta) dias, sua Conta de Pagamento
                será encerrada independente de aviso prévio.
              </CardText>

              <CardText className="mb-2">8. Remuneração</CardText>

              <CardText className="mb-2">
                8.1. Em contrapartida à prestação dos serviços de tecnologia que
                integram o Sistema, custódia e gestão de recurso na Conta de
                Pagamento e licença de uso das Funcionalidades, o USUÁRIO pagará
                à PARCEIRA ou à Instituição de Pagamento (conforme aplicável),
                as tarifas, fixas ou percentuais, incidentes sobre cada
                Transação realizada.
              </CardText>

              <CardText className="mb-2">
                8.2.O USUÁRIO pagará: (i) tarifa por cada Transação realizada no
                Sistema; (ii) tarifa de saque dos recursos; (iii) tarifa por
                inatividade da Conta de Pagamento; (iv) tarifa para a
                transferência de recursos para a conta corrente de terceiros
                (caso disponível); e (v) tarifas adicionais por outros serviços
                a serem contratados, de forma cumulativa com as demais tarifas.
              </CardText>

              <CardText className="mb-2">
                8.2.1. O valor das tarifas será informado ao USUÁRIO no Cadastro
                e/ou divulgado na Plataforma no momento da realização da
                Transação.
              </CardText>

              <CardText className="mb-2">
                8.3. Os valores das tarifas cobrados são variáveis de acordo com
                a natureza de cada Transação, encontrando-se sempre disponíveis
                para consulta pelo USUÁRIO na Plataforma ou mediante solicitação
                por meio dos canais de atendimento disponíveis.
              </CardText>

              <CardText className="mb-2">
                8.4. Para a cobrança das tarifas, inclusive por serviços
                adicionais que vierem a ser contratados pelo USUÁRIO, a
                Instituição de Pagamento poderá, alternativamente: (i) realizar
                lançamentos de débitos na Conta de Pagamento; ou (ii) compensar
                o valor dos débitos com quaisquer outros créditos, presentes ou
                futuros, devidos ao USUÁRIO.
              </CardText>

              <CardText className="mb-2">
                8.4.1. Caso não haja recursos suficientes para o pagamento das
                tarifas, a PARCEIRA solicitará ao USUÁRIO o crédito imediato de
                recursos em sua Conta de Pagamento. Tão logo haja recursos na
                Conta de Pagamento, os valores serão debitados automaticamente e
                sem prévio aviso.
              </CardText>

              <CardText className="mb-2">
                8.4.2. Sem prejuízo da suspensão dos serviços prestados por meio
                do Sistema, caso o USUÁRIO deixe de realizar o crédito em sua
                Conta de Pagamento, haverá a incidência dos encargos moratórios
                estipulados neste Contrato.
              </CardText>

              <CardText className="mb-2">
                8.5. A PARCEIRA ou a Instituição de Pagamento (conforme
                aplicável) poderá efetuar o reajuste ou alteração do valor das
                tarifas cobradas, informando previamente o USUÁRIO, por e-mail
                ou divulgação prévia na Plataforma.
              </CardText>
              <CardText className="mb-2">
                8.5.1. Caso o USUÁRIO não concorde com as novas condições de
                remuneração, poderá encerrar este Contrato, sem a incidência de
                quaisquer ônus ou penalidades. O não encerramento será
                interpretado como anuência com relação aos novos valores das
                tarifas cobradas.
              </CardText>
              <CardText className="mb-2">
                8.5.2. Caso sejam criados novos tributos ou alteradas as
                condições de cálculo e/ou cobrança de tributos incidentes sobre
                a remuneração vigente, a PARCEIRA ou a Instituição de Pagamento
                (conforme aplicável), mediante aviso prévio de 10 (dez) dias,
                irá automaticamente reajustar os valores cobrados de forma a
                restabelecer o equilíbrio econômico-financeiro.
              </CardText>
              <CardText className="mb-2">
                8.6. A Instituição de Pagamento poderá instituir outras
                modalidades de remuneração, inclusive sobre os serviços
                adicionais que vierem a ser pactuados em instrumentos
                contratuais próprios, mediante prévia comunicação ao USUÁRIO,
                com antecedência de 10 (dez) dias.
              </CardText>
              <CardText className="mb-2">
                8.7. Após o credenciamento no Sistema, o USUÁRIO poderá, a
                qualquer momento, solicitar o cancelamento de sua adesão e
                encerramento de sua Conta de Pagamento; mediante prévia
                comunicação formal à PARCEIRA, com antecedência de 30 (trinta)
                dias.
              </CardText>

              <CardText className="mb-2">
                9. Prazo de Vigência e Término
              </CardText>

              <CardText className="mb-2">
                9.1. Este Contrato é celebrado por prazo indeterminado, e passa
                a vigorar a partir da data de sua aceitação pelo USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                9.2. Este Contrato será extinto, a qualquer momento, mediante
                notificação com antecedência de 30 (trinta) dias, por qualquer
                das Partes.
              </CardText>
              <CardText className="mb-2">
                9.2.1. Salvo quanto às hipóteses abaixo, a extinção deste
                Contrato se dará sem a incidência de quaisquer ônus, encargos ou
                penalidades; ressalvadas as obrigações pendentes e que deverão
                ser devidamente cumpridas pelo prazo necessário.
              </CardText>
              <CardText className="mb-2">
                9.3. Haverá a rescisão imediata e motivada deste Contrato, nas
                hipóteses de: (i) decretação de falência, pedido de recuperação
                judicial ou extrajudicial ou encerramento das atividades de
                qualquer das Partes, ao exclusivo critério da outra Parte; ou
                (ii) o descumprimento de qualquer obrigação estabelecida neste
                Contrato que não seja sanada no prazo estipulado ou, na omissão,
                no prazo de 10 (dez) dias contados da notificação ou aviso.
              </CardText>
              <CardText className="mb-2">
                9.4. Caso a rescisão do Contrato ocorra por culpa do USUÁRIO,
                fica desde já estabelecido que seu acesso à Plataforma e ao
                Sistema será imediatamente bloqueado, com a suspensão de sua
                Conta de Pagamento e retenção dos créditos do USUÁRIO pelo prazo
                necessário para que possam ser resguardados os direitos da
                PARCEIRA, da Instituição de Pagamento, de outros USUÁRIOS e de
                terceiros; sem prejuízo da adoção de outras medidas legais
                necessárias e da apuração e ressarcimento de eventuais danos
                complementares.
              </CardText>

              <CardText className="mb-2">
                10. Responsabilidades Adicionais do USUÁRIO
              </CardText>

              <CardText className="mb-2">
                10.1. Os tributos incidentes na prestação dos serviços são de
                exclusiva responsabilidade do USUÁRIO, podendo a PARCEIRA ou a
                Instituição de Pagamento (conforme aplicável) descontar os
                respectivos valores dos créditos do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                10.2. A realização da transferência bancária de recursos,
                pagamento do boleto bancário, dentre outras operações, poderão
                estar sujeitas à cobrança de tarifas, taxas ou encargos, de
                acordo com os critérios e valores estabelecidos pelas
                instituições financeiras; sendo que a PARCEIRA não possui
                qualquer ingerência sobre os valores cobrados do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                10.3. O USUÁRIO reconhece e concorda que a realização das
                Transações pelo Sistema está sujeita a aplicação da legislação
                vigente, inclusive de prevenção a lavagem de dinheiro,
                financiamento ao terrorismo e combate à corrupção; estando os
                repasses dos valores das Transações sujeitos ao estrito
                cumprimento da legislação aplicável.
              </CardText>
              <CardText className="mb-2">
                10.4. Para utilização das Funcionalidades e acesso ao Sistema, o
                USUÁRIO deverá possuir equipamentos (computador, smartphone,
                tablet ou outros dispositivos similares) com acesso à internet e
                em condições compatíveis para seu uso, sendo de exclusiva
                responsabilidade do USUÁRIO a obtenção, manutenção e custeio de
                tal acesso e dos equipamentos necessários (incluindo tributos,
                tarifas ou encargos cobrados pelos fornecedores de serviços). A
                PARCEIRA e a Instituição de Pagamento não serão responsáveis
                pela não realização da Transação em razão da incompatibilidade
                dos equipamentos ou pela ausência ou falha no acesso à internet.
              </CardText>
              <CardText className="mb-2">
                10.5. O USUÁRIO compromete-se a isentar a PARCEIRA e a
                Instituição de Pagamento de toda e qualquer reclamação ou
                litígio judicial ou extrajudicial decorrente da utilização do
                Sistema, inclusive no que se refere às próprias atividades do
                USUÁRIO e às questões relacionadas com os negócios jurídicos
                celebrados pelo USUÁRIO fora do Sistema.
              </CardText>
              <CardText className="mb-2">
                10.6. O USUÁRIO obriga-se a ressarcir a PARCEIRA e a Instituição
                de Pagamento de todos os valores comprovadamente despendidos em
                ações judiciais ou processos administrativos que tenham sido
                ajuizados contra a PARCEIRA ou a Instituição de Pagamento em
                razão do descumprimento de obrigações imputáveis ao USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                10.6.1. A Instituição de Pagamento poderá reter os créditos a
                serem pagos ao USUÁRIO e compensá-los para o pagamento de
                débitos decorrentes de condenações, custas e despesas
                processuais, além de honorários advocatícios.
              </CardText>
              <CardText className="mb-2">
                10.7. A PARCEIRA e a Instituição de Pagamento possuem uma
                Política de Privacidade que indica como as informações do
                USUÁRIO são serão coletadas, utilizadas, armazenadas, tratadas,
                compartilhadas, divulgadas e protegidas. O USUÁRIO deve ler
                atentamente a Política de Privacidade, que é parte integrante
                deste Contrato.
              </CardText>
              <CardText className="mb-2">
                10.7.1. A PARCEIRA e a Instituição de Pagamento irão adotar
                todas as medidas necessárias e se utilizar de tecnologias
                adequadas para proteger a coleta, processamento e armazenamento
                das informações do USUÁRIO; porém não tem como assegurar que
                terceiros não autorizados se utilizem de meios fraudulentos para
                furto, uso indevido, alteração ou acesso não autorizado às
                informações do USUÁRIO.
              </CardText>

              <CardText className="mb-2">
                11. Licença de Uso e Propriedade Intelectual das Funcionalidades
              </CardText>

              <CardText className="mb-2">
                11.1. A PARCEIRA, na qualidade de licenciada, autoriza o uso
                pelo USUÁRIO das Funcionalidades que integram o Sistema, durante
                o prazo de vigência deste Contrato, mediante os termos e
                condições ora estabelecidos.
              </CardText>
              <CardText className="mb-2">
                11.2. O USUÁRIO reconhece e concorda que a propriedade
                intelectual das Funcionalidades é de integral e exclusiva
                titularidade da PARCEIRA, na qualidade de licenciada.
              </CardText>
              <CardText className="mb-2">
                11.2.1. É vedado ao USUÁRIO: (i) copiar ou transferir de
                qualquer forma, total ou parcialmente, quaisquer Funcionalidades
                ou informações relativas às Funcionalidades; (ii) modificar as
                características das Funcionalidades ou realizar sua integração
                com outros sistemas ou softwares; (iii) copiar os dados
                extraídos do Sistema, exceto aqueles relativos às movimentações
                da Conta de Pagamento.
              </CardText>
              <CardText className="mb-2">
                11.3. O USUÁRIO compromete-se a não infringir quaisquer direitos
                relativos às marcas, patentes, software, domínio na internet,
                segredo industrial ou, ainda, direito de propriedade industrial
                ou direito autoral de quaisquer serviços ou Funcionalidades
                disponibilizados no âmbito deste Contrato, bem como a não usar o
                nome, marca, logomarca ou qualquer tipo de sinal distintivo da
                PARCEIRA ou de seus parceiros, sem o consentimento prévio e
                escrito.
              </CardText>

              <CardText className="mb-2">12. Serviços de Terceiros</CardText>

              <CardText className="mb-2">
                12.1. A Instituição de Pagamento e a PARCEIRA, conforme
                aplicável, poderão celebrar parcerias com Fornecedores,
                possibilitando ao USUÁRIO a possibilidade de contratação de
                diversos serviços e produtos disponíveis na Plataforma.
              </CardText>
              <CardText className="mb-2">
                12.2. No que se refere aos Serviços de Terceiros, a Instituição
                de Pagamento e a PARCEIRA são meras intermediadoras da relação
                entre o USUÁRIO e o Fornecedor, uma vez que a tecnologia
                disponível na Plataforma tem a finalidade de aproximar as
                partes.
              </CardText>
              <CardText className="mb-2">
                12.2.1. Serão disponibilizados na Plataforma informações sobre
                os Fornecedores, seus produtos e serviços;
              </CardText>
              <CardText className="mb-2">
                12.2.2. Para utilização dos Serviços de Terceiros, o USUÁRIO
                poderá ou não ser direcionado à um site ou outra plataforma.
                Além disso, poderá ser exigido pelos Fornecedores novas
                informações, documentos e/ou dados relacionados ao USUÁRIO.
              </CardText>

              <CardText className="mb-2">
                12.3. Por se tratar de uma atividade de intermediação, a
                Instituição de Pagamento e a PARCEIRA não possuem qualquer
                interferência nas condições, preços e execução dos Serviços de
                Terceiros, inclusive sobre às condições de contratação,
                solicitação de documentos e demais atos pertinentes. Os
                Fornecedores serão única e exclusivamente responsáveis por todas
                as questões relacionadas aos Serviços de Terceiros oferecidos na
                Plataforma.
              </CardText>
              <CardText className="mb-2">
                12.3.1. A Instituição de Pagamento e a PARCEIRA não poderão, em
                nenhuma hipótese, ser responsabilizada por quaisquer reclamações
                decorrentes da contratação dos Serviços de Terceiros, cabendo ao
                USUÁRIO contatar diretamente o Fornecedor responsável pela sua
                prestação.
              </CardText>
              <CardText className="mb-2">
                12.3.2. O Fornecedor será exclusivamente responsável por todas
                as informações divulgadas na Plataforma acerca dos Serviços de
                Terceiros, inclusive as imagens, suas características e
                respectivos preços, assim como pela qualidade, existência,
                quantidade, segurança, entrega e garantia do quanto prometido;
                de forma que a Instituição de Pagamento não exercerá qualquer
                controle ou fiscalização e não terá qualquer responsabilidade
                sobre os Serviços de Terceiros e/ou a veracidade das informações
                disponibilizadas na Plataforma.
              </CardText>
              <CardText className="mb-2">
                12.3.3. Além disso, a Instituição de Pagamento e a PARCEIRA não
                se responsabiliza pela idoneidade, capacidade
                técnico-operacional e financeira dos Fornecedores, atuando
                apenas como mera intermediadora da sua relação com o USUÁRIO.
              </CardText>

              <CardText className="mb-2">13. Disposições Gerais</CardText>

              <CardText className="mb-2">
                13.1. O USUÁRIO declara-se ciente e concorda que,
                independentemente do local de onde esteja utilizando os serviços
                que integram o Sistema, a relação entre as Partes será sempre
                regida pela legislação brasileira.
              </CardText>

              <CardText className="mb-2">
                13.2. O USUÁRIO expressamente autoriza a PARCEIRA a utilizar as
                informações, ainda que relativas ao seu Cadastro, Conta de
                Pagamento, conta corrente e Transações realizadas no Sistema,
                para formação de banco de dados, preservando-se a
                individualidade e identificação de cada USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                13.3. O USUÁRIO autoriza a PARCEIRA a verificar e trocar
                informações cadastrais, creditícias e/ou financeiras a seu
                respeito em âmbito nacional, com entidades financeiras ou de
                proteção ao crédito, inclusive a efetuar consultas a sistemas de
                risco de crédito sobre eventuais débitos de responsabilidades do
                USUÁRIO e a prestar ao órgão citado informações dos dados
                cadastrais e informações creditícias.
              </CardText>
              <CardText className="mb-2">
                13.4. Nos termos da legislação aplicável e da Política de
                Privacidade, o USUÁRIO concorda que a PARCEIRA ou qualquer de
                seus parceiros enviem mensagens de caráter informativo ou
                publicitário.
              </CardText>
              <CardText className="mb-2">
                13.5. A Instituição de Pagamento irá auxiliar e cooperar com
                qualquer autoridade judicial, reguladora ou órgão público que
                venha a solicitar informações, podendo, neste caso, fornecer
                quaisquer informações sobre o USUÁRIO em relação à utilização da
                Plataforma.
              </CardText>
              <CardText className="mb-2">
                13.6. As Partes elegem o Foro da Cidade de São Paulo / SP como
                único competente para dirimir as questões decorrentes deste
                Contrato, com renúncia expressa a qualquer outro, por mais
                privilegiado que seja.
              </CardText>

              <CardText className="mb-2">
                Versão atualizada em 27 de Agosto de 2020.
              </CardText>

              <CardText className="mb-2">Anexo</CardText>

              <CardText className="mb-2">
                Termo de Utilização do Cartão Pré-Pago
              </CardText>

              <CardText className="mb-2">
                Este Termo de Utilização do Cartão Pré-Pago é anexo (“Anexo”) e
                parte integrante dos Termos e Condições de Uso da Plataforma da
                [PARCEIRA] (“Termo”) e tem por objetivo estabelecer as condições
                para utilização do Cartão Pré-Pago pelo USUÁRIO.
              </CardText>

              <CardText className="mb-2">
                Ao utilizar o Cartão Pré-Pago, o USUÁRIO automaticamente
                concorda com todas as regras deste Anexo, responsabilizando-se
                integralmente por todos e quaisquer atos praticados.
              </CardText>
              <CardText className="mb-2">
                Aplicam-se integralmente a este Anexo, todos os termos,
                condições, limites e responsabilidades previstas nos Termos e
                Condições para Abertura de Conta de Pagamento, o qual foi
                expressamente aceito pelo USUÁRIO antes de solicitar o Cartão
                Pré-Pago.
              </CardText>
              <CardText className="mb-2">
                Caso não concorde com qualquer dos termos e condições abaixo
                estabelecidos, o USUÁRIO não deve utilizar o Cartão Pré-Pago.
              </CardText>

              <CardText className="mb-2">
                Os serviços relacionados com o Cartão Pré-Pago serão prestados
                pela ELOHE SOLUÇÕES LTDA. inscrita no CNPJ/MF sob o nº
                10.268.349/0001-62, com sede na ST CENTRAL C7 LOTE 11 - Bairro
                TAGUATINGA CENTRO, na cidade de BRASÍLIA, estado de DISTRITO
                FEDERAL, CEP: 72.010-050 (“PARCEIRA”), em parceria com FLAGSHIP
                Consultoria em Tecnologia da Informação Ltda., inscrita no
                CNPJ/MF sob o nº 23.114.447/0001-97, com sede na Rua Dr. Ricardo
                Benetton Martins, s/n, Prédio16 (Térreo), Polo II de Alta
                Tecnologia, CEP 13086-902 na cidade de Campinas, estado de São
                Paulo (“Instituição de Pagamento”) e pela BIZ TECNOLOGIA EM
                MEIOS DE PAGAMENTO LTDA., sociedade empresária com sede na
                Avenida São Gabriel, 555, 7° Andar, conj. 704, Jardim Paulista,
                CEP 01435-001, na cidade de São Paulo, Estado de São Paulo,
                inscrita no CNPJ sob o n° 07.155.434/0001-00 (“Emissora”).
              </CardText>

              <CardText className="mb-2">1. Definições</CardText>

              <CardText className="mb-2">
                1.1. Sem prejuízo das definições atribuídas no Termo e que
                também se aplicam a este Anexo, as palavras abaixo, quando
                utilizadas no singular ou plural, terão os seguintes
                significados:
              </CardText>
              <CardText className="mb-2">
                “Bandeira”: o arranjo de pagamento instituído pela Visa,
                licenciadora da marca e responsável pelos sistemas que permitem
                a emissão do Cartão Pré-Pago e credenciamentos dos
                Estabelecimentos.
              </CardText>
              <CardText className="mb-2">
                “Chargeback”: é o procedimento de contestação por meio do qual o
                USUÁRIO declara não reconhecer uma despesa efetuada com seu
                Cartão Pré-Pago.
              </CardText>
              <CardText className="mb-2">
                “Estabelecimento”: qualquer fornecedor de produtos e/ou serviços
                que está habilitado a aceitar pagamentos com o Cartão Pré-Pago,
                no Brasil e/ou no exterior, em lojas físicas ou por meio da
                internet.
              </CardText>

              <CardText className="mb-2">2. Objeto</CardText>

              <CardText className="mb-2">
                2.1. Pelo presente Anexo, o USUÁRIO adquire o Cartão Pré-Pago
                físico, vinculado à Conta de Pagamento que será aberta perante a
                Emissora, possibilitando ao USUÁRIO aquisição de bens e serviços
                perante os Estabelecimentos filiados à Bandeira indicada no
                respectivo cartão.
              </CardText>
              <CardText className="mb-2">
                2.2. O prazo para envio do Cartão Pré-Pago será informado na
                Plataforma e pelos canais de atendimento da PARCEIRA.
              </CardText>
              <CardText className="mb-2">
                2.3. Uma vez aprovada a emissão do Cartão Pré-Pago, o USUÁRIO,
                na qualidade de portador de um Cartão Visa, contará com os
                benefícios concedidos pela Visa e seus parceiros.
              </CardText>

              <CardText className="mb-2">3. Credenciamento</CardText>

              <CardText className="mb-2">
                3.1. Para aquisição do Cartão Pré-Pago, o USUÁRIO deverá estar
                credenciado à PARCEIRA.
              </CardText>
              <CardText className="mb-2">
                3.2. Ao aderir a este Anexo, o USUÁRIO concorda com a abertura
                de uma nova Conta de Pagamento individual e exclusiva, de sua
                exclusiva titularidade, perante a Emissora
              </CardText>
              <CardText className="mb-2">
                3.3. Caso, após a emissão do Cartão Pré-Pago, a PARCEIRA, a
                Instituição de Pagamento e a Emissora constatem que o USUÁRIO
                forneceu informações falsas, incompletas, equivocadas, errôneas,
                enganosas, ou, ainda, que não permita identificar a identidade
                do USUÁRIO, elas poderão (i) solicitar esclarecimentos e
                documentação adicional que julgarem necessários para a devida
                comprovação das informações prestadas e para a validação do
                cadastro, podendo, inclusive, recusarem-se a validar qualquer
                cadastro, a seu exclusivo critério ou (ii) automaticamente
                excluir o seu cadastro, suspender ou cancelar o Cartão Pré-Pago.
              </CardText>
              <CardText className="mb-2">
                3.4. A PARCEIRA, a Instituição de Pagamento e a Emissora não
                serão responsáveis pelo uso indevido do Cartão Pré-Pago por
                terceiros, bem como por qualquer dano direto ou indireto que
                resulte do mau uso ou da inabilidade do uso do Cartão Pré-Pago
                pelo USUÁRIO ou por quaisquer terceiros, sendo certo que,
                havendo evidências ou indícios de uso irregular, inadequado, ou
                suspeito do Cartão Pré-Pago, o USUÁRIO poderá ter seu cadastro
                imediatamente suspenso ou cancelado, sem prejuízo das demais
                sanções legais e contratuais.
              </CardText>

              <CardText className="mb-2">
                4. Utilização do Cartão Pré-Pago
              </CardText>

              <CardText className="mb-2">
                4.1. Após a aprovação do seu credenciamento perante a PARCEIRA e
                a Emissora, USUÁRIO poderá realizar transações de pagamento com
                a utilização do Cartão Pré-Pago, desde que possua
                disponibilidade de saldo, dentre as quais:
              </CardText>
              <CardText className="mb-2">
                (a) Compras em Estabelecimentos: o USUÁRIO poderá realizar
                compras em qualquer Estabelecimento que seja fornecedor de
                produtos e/ou serviços que estejam habilitados a aceitar
                pagamentos com o seu Cartão Pré-Pago, no Brasil e/ou no
                Exterior, em lojas físicas ou por meio da internet; e
              </CardText>
              <CardText className="mb-2">
                (b) Saque: retirada de recursos em terminais eletrônicos
                habilitados, mediante uso do Cartão Pré-Pago. Nesta hipótese,
                poderão ser cobradas taxas adicionais pelas empresas
                administradoras desses terminais.
              </CardText>
              <CardText className="mb-2">
                4.2. Assim que receber o Cartão Pré-Pago, o USUÁRIO ficará
                responsável por conferir os seus dados, sendo que o Cartão
                Pré-Pago será entregue bloqueado, por medida de segurança e o
                desbloqueio deverá ser realizado por meio dos canais de
                atendimento indicados na Plataforma.
              </CardText>
              <CardText className="mb-2">
                4.3. O USUÁRIO deverá se atentar para o regulamento do arranjo
                de pagamento da Visa, que possui termos condições específicas
                para a sua utilização.
              </CardText>
              <CardText className="mb-2">
                4.4. O USUÁRIO é o único responsável pelo uso e guarda do seu
                Cartão Pré-Pago. Recomenda-se que o USUÁRIO que: (i) guarde seu
                Cartão Pré-Pago em local seguro, jamais permitindo seu uso por
                terceiros; (ii) memorize sua senha e mantenha-a em sigilo,
                evitando anotar ou guardar a senha em suportes físicos ou
                digitais, e; (iii) nunca anote ou guarde a senha em conjunto com
                seu Cartão Pré-Pago; e, ainda, (iv) não utilize senhas
                relacionadas a datas ou outras referências pessoais do USUÁRIO e
                que efetue rotineiramente a troca de sua senha como medida de
                segurança
              </CardText>
              <CardText className="mb-2">
                4.5. Em caso de perda, extravio, furto ou roubo do Cartão
                Pré-Pago, o USUÁRIO deve entrar em contato imediatamente por um
                dos canais de atendimento indicados no Cartão Pré-Pago ou na
                Plataforma da PARCEIRA. Após o contato, o uso e acesso ao Cartão
                Pré-Pago poderá ser temporariamente bloqueado, até que o USUÁRIO
                receba novo cartão.
              </CardText>
              <CardText className="mb-2">
                4.5.1. Com o cancelamento do cartão bloqueado, um novo Cartão
                Pré-Pago será emitido para o USUÁRIO, e será entregue nos prazos
                previamente informados. Poderão ser cobradas tarifas adicionais
                pela emissão do novo Cartão Pré-Pago, as quais serão
                automaticamente descontadas da Conta de Pagamento do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                4.6. A PARCEIRA, a Instituição de Pagamento e/ou a Emissora não
                são responsáveis pelas transações de pagamento realizadas pelos
                titulares do Cartão Pré-Pago, uma vez que não são parte de
                qualquer operação de compra e venda perante os Estabelecimentos.
              </CardText>

              <CardText className="mb-2">5. Chargeback</CardText>

              <CardText className="mb-2">
                5.1. Caso o USUÁRIO não reconheça uma operação feita com o seu
                Cartão Pré-Pago ou problemas com saques em ATM, deverá entrar em
                contato pelos canais de atendimento no prazo máximo de 90
                (noventa) dias e seguir as orientações para realização do
                procedimento de Chargeback orientado pela própria emissora do
                Cartão.
              </CardText>
              <CardText className="mb-2">
                5.1.1. O procedimento e a documentação exigida para o Chargeback
                seguirão as regras estabelecidas pela Bandeira e Emissora, de
                forma que o reconhecimento do Chargeback dependerá do envio de
                documentos e esclarecimentos que demonstrem a veracidade e
                licitude do negócio que deu origem à transação, sem que tenha
                havido culpa ou dolo do USUÁRIO.
              </CardText>
              <CardText className="mb-2">
                5.1.2. Caso seja acatado o Chargeback, o valor será creditado na
                Conta de Pagamento do USUÁRIO, no prazo estabelecido pela
                Emissora. Não sendo reconhecido o Chargeback, a transação de
                pagamento ou saque, serão mantidas.
              </CardText>
              <CardText className="mb-2">
                5.2. O USUÁRIO está ciente e concorda que o procedimento de
                Chargeback somente ocorrerá para transações de pagamento
                efetuadas com a utilização do Cartão Pré-Pago para compras
                online ou sem cartão presente.
              </CardText>
              <CardText className="mb-2">
                5.2.1. Não serão aceitos os procedimentos de Chargeback para
                transações com a utilização realizadas por meio das Contas de
                Pagamento, que tenham sido efetuadas com a utilização de cartão
                físico, no qual foi inserido o chip e digitada a senha do
                USUÁRIO.
              </CardText>

              <CardText className="mb-2">6. Cancelamento do Cartão</CardText>

              <CardText className="mb-2">
                6.1. O USUÁRIO poderá, a qualquer momento, solicitar o
                cancelamento de seu Cartão Pré-Pago, mediante solicitação
                realizada pelos canais de atendimento da PARCEIRA.
              </CardText>
              <CardText className="mb-2">
                6.2. Além das disposições de cancelamento estabelecidas no
                Termo, a PARCEIRA poderá bloquear o Cartão Pré-Pago do USUÁRIO
                nas seguintes hipóteses:
              </CardText>
              <CardText className="mb-2">
                (a) Caso o USUÁRIO viole quaisquer das disposições deste Anexo
                ou dos Termos;
              </CardText>
              <CardText className="mb-2">
                (b) Caso sejam verificadas transações de pagamento fora do
                padrão de uso, e o USUÁRIO deixe de atender pedido de envio de
                documentos ou informações para a comprovação de sua
                regularidade; ou
              </CardText>
              <CardText className="mb-2">
                (c) Em caso de falecimento ou insolvência do USUÁRIO.
              </CardText>

              <CardText className="mb-2">7. Disposições Gerais</CardText>

              <CardText className="mb-2">
                7.1. Este Anexo pode ser alterado, a qualquer tempo, a fim de
                refletir eventuais alterações para a utilização do Cartão
                Pré-Pago, mediante comunicação ao USUÁRIO. Caso o USUÁRIO não
                concorde com os novos termos, poderá rejeitá-los, e realizar o
                cancelamento do Cartão Pré-Pago.
              </CardText>
              <CardText className="mb-2">
                7.2. Se o USUÁRIO utilizar o Cartão Pré-Pago após alteração
                deste Anexo, entende-se que concorda com todas as modificações
                realizadas.
              </CardText>
              <CardText className="mb-2">
                7.3. O USUÁRIO desde já concorda que a PARCEIRA, a Instituição
                de Pagamento e a Emissora comuniquem ao Banco Central do Brasil,
                ao Conselho de Controle de Atividades Financeiras ou outros
                órgãos que a legislação previr, as operações que possam estar
                configuradas na Lei 9.613/98 (que dispõe sobre os crimes de
                lavagem ou ocultação de bens, direitos e valores) e demais
                disposições legais pertinentes à matéria.
              </CardText>
            </article>

            <Form onSubmit={onSubmit}>
              <FormGroup>
                <CustomInput
                  type="checkbox"
                  label={<Terms />}
                  className="custom-control-Primary"
                  id="terms"
                  name="terms"
                  onChange={(e) => setHasAccept(e.target.checked)}
                  checked={hasAccept}
                />
              </FormGroup>
              {hasAccept ? (
                <Button type="submit" color="primary" block>
                  Aceitar
                </Button>
              ) : null}
            </Form>
          </Col>
        </Col>
      </Row>
    </div>
  );
};
